/**
 * Analytics — Meta (Facebook + Instagram) Pixel + Conversions API.
 *
 * Iter R30 redesign:
 * - Pixel base + PageView ora caricato DIRETTAMENTE in public/index.html
 *   (vedi script inline lì). Questo modulo gestisce eventi successivi.
 * - Ogni evento genera un `event_id` univoco e viene inviato sia al browser
 *   pixel sia al backend CAPI (`POST /api/meta/conversion`) per deduplication
 *   server-side: resiste a iOS 14.5+ ATT, ad-blocker, cookie reject parziale.
 *
 * GDPR: gli eventi custom (Lead/CompleteRegistration ecc.) sono gated dietro
 * il consenso marketing. PageView base è considerato audience-measurement
 * tecnico e parte sempre (cfr. linee guida Garante Privacy 2021 per pixel
 * tecnici di prima parte).
 */

import { hasMarketingConsent } from "./cookieConsent";

const API_BASE = process.env.REACT_APP_BACKEND_URL || "";

function newEventId(prefix = "ev") {
  return `${prefix}_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * Compat: chiamata legacy da App.js. Ora che il pixel base è in HTML,
 * questa funzione è no-op a meno che siamo in dev e l'HTML è stato
 * servito senza la sostituzione di build (es. preview senza env var).
 *
 * Mantiene anche il listener per il consenso marketing futuro (per ora
 * gli eventi sono inviati direttamente da `track`).
 */
export function initAnalytics() {
  // No-op: pixel base ora inline in public/index.html (Iter R30).
}

/**
 * Invia l'evento al backend per Conversions API server-side.
 * Usa `event_id` per dedup con il client pixel (Meta li unisce automaticamente).
 *
 * Non-blocking: errori silenziati per non rompere UX.
 */
async function sendCapiEvent(eventName, eventId, params, isCustom) {
  if (!API_BASE) return;
  try {
    await fetch(`${API_BASE}/api/meta/conversion`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({
        event_name: eventName,
        event_id: eventId,
        is_custom: isCustom,
        params: params || {},
        event_source_url: typeof window !== "undefined" ? window.location.href : "",
        // user_data: il backend integra IP + UA + fbp/fbc cookie automaticamente
      }),
    });
  } catch {
    /* ignore — il client pixel è già passato */
  }
}

/**
 * Traccia un evento. Standard Meta: PageView, Lead, CompleteRegistration,
 * ViewContent, AddToCart, InitiateCheckout, Purchase.
 *
 * @param {string} eventName - nome evento
 * @param {object} params - parametri (currency, value, content_name, ...)
 * @param {boolean} isCustom - se true usa trackCustom (Custom Conversion)
 */
export function track(eventName, params = {}, isCustom = false) {
  if (typeof window === "undefined") return;
  // GDPR: gli eventi custom diversi da PageView richiedono consenso marketing
  if (!hasMarketingConsent()) return;

  const eventId = newEventId("ev");

  // 1. Client-side pixel (browser)
  if (window.fbq) {
    try {
      if (isCustom) {
        window.fbq("trackCustom", eventName, params, { eventID: eventId });
      } else {
        window.fbq("track", eventName, params, { eventID: eventId });
      }
    } catch {
      /* ignore */
    }
  }

  // 2. Server-side via Conversions API (resiste a ad-blocker / iOS ATT)
  // Stesso event_id → Meta deduplica automaticamente.
  sendCapiEvent(eventName, eventId, params, isCustom);
}

// Helpers per eventi specifici della landing — chiamati dai componenti.

export const trackViewLanding = () =>
  track("ViewContent", { content_name: "landing_haccp_elog", content_category: "saas_haccp" });

export const trackPricingCardClick = (packageKey, monthlyPrice) =>
  track("InitiateCheckout", {
    content_name: `package_${packageKey}`,
    content_category: "pricing",
    currency: "EUR",
    value: monthlyPrice,
  });

export const trackDemoCtaClick = (source) =>
  track("Lead", { content_category: "demo_cta_click", content_name: source });

export const trackDemoFormSubmit = (packageKey) =>
  track("AddToCart", { content_name: `demo_form_submit_${packageKey}`, currency: "EUR", value: 0 });

export const trackDemoSuccess = (packageKey, monthlyPrice) => {
  track("CompleteRegistration", {
    content_name: `demo_signup_${packageKey}`,
    currency: "EUR",
    value: monthlyPrice,
    predicted_ltv: monthlyPrice * 12,
    status: "demo_14d",
  });
  // Custom Conversion legata al setup Ads Manager esistente
  track(
    "Demo Signup Completato",
    { content_name: `demo_signup_${packageKey}`, currency: "EUR", value: monthlyPrice },
    true,
  );
};
